import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { QueryClient } from '@tanstack/react-query';
import {
  apiAdminworkspaceConfigsIdPatch,
  apiAdminworkspaceConfigsPost
} from '@r/api/generated/client.ts';
import type { WorkspaceConfigWorkspaceConfigReadModel } from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import type { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

/**
 * Un switch admin qui persiste un booléen sur une workspace config (PATCH si la row existe déjà,
 * POST sinon), avec optimistic update + revert automatique en cas d'échec. Factorise le pattern
 * partagé par les switchs "afficher référentiel" et "masquer montants" de ProductsReferences.
 */
export function useHideableWorkspaceConfigSwitch(options: {
  configType: string;
  configKey: string;
  configIri: string | null;
  workspaceConfigsData: WorkspaceConfigWorkspaceConfigReadModel[] | undefined;
  isDiscoveryLoading: boolean;
  currentValue: boolean;
  queryClient: QueryClient;
  t: (key: string) => string;
  showOrvalErrorToast: ReturnType<typeof useOrvalErrorToast>;
}) {
  const {
    configType,
    configKey,
    configIri,
    workspaceConfigsData,
    isDiscoveryLoading,
    currentValue,
    queryClient,
    t,
    showOrvalErrorToast
  } = options;

  const [configId, setConfigId] = useState<number | null>(null);
  // Contexte déjà chargé au premier render (app.tsx bloque le rendu tant qu'il n'est pas prêt) :
  // init direct plutôt que `false` + effet, pour éviter le flicker OFF→bonne-valeur au montage.
  const [value, setValue] = useState(currentValue);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setValue(currentValue);
  }, [currentValue]);

  useEffect(() => {
    if (configId || !workspaceConfigsData) return;

    const id = workspaceConfigsData.find(
      (wc) => wc.config?.type === configType && wc.config?.key === configKey
    )?.id;
    if (id) setConfigId(id);
  }, [configId, workspaceConfigsData, configType, configKey]);

  const persist = useCallback(
    async (newValue: boolean, persistOptions?: { silent?: boolean; isActivated?: boolean }) => {
      const isActivated = persistOptions?.isActivated ?? newValue;
      const silent = persistOptions?.silent ?? false;

      setIsUpdating(true);

      try {
        const response = configId
          ? await apiAdminworkspaceConfigsIdPatch(configId.toString(), {
              value: newValue.toString()
            })
          : configIri
            ? await apiAdminworkspaceConfigsPost({
                value: newValue.toString(),
                config: configIri
              })
            : null;

        if (response) {
          if (response.id) setConfigId(response.id);
          queryClient.invalidateQueries({ queryKey: ['workspaceConfigs'] });
          if (!silent) {
            toast.success(
              isActivated ? t('toast.success.activated') : t('toast.success.deactivated')
            );
          }
          return true;
        }

        return false;
      } catch (error) {
        console.error('Error updating workspace config:', error);

        // silent = action de housekeeping automatique (pas un clic utilisateur) : pas de toast
        // du tout si ça échoue, un message d'erreur sur un switch que personne n'a touché serait
        // incompréhensible.
        if (!silent) {
          const errorMessage = isActivated
            ? t('toast.error.activation_failed')
            : t('toast.error.deactivation_failed');

          if (isOrvalErrorFromPath(error, 'patch', '/admin/workspaceConfigs')) {
            showOrvalErrorToast(error, errorMessage);
          } else {
            toast.error(errorMessage);
          }
        }

        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [configId, configIri, queryClient, t, showOrvalErrorToast]
  );

  const toggle = useCallback(
    async (newValue: boolean, toggleOptions?: { silent?: boolean; isActivated?: boolean }) => {
      setValue(newValue);
      const succeeded = await persist(newValue, toggleOptions);
      if (!succeeded) setValue(!newValue);
      return succeeded;
    },
    [persist]
  );

  const isReady = !isDiscoveryLoading && (configId !== null || !!configIri);

  return { value, isUpdating, isReady, toggle };
}
